import React from "react";
import { motion } from "framer-motion";

export const PeerOSDiagram = () => {
    return (
        <div className="w-full h-[400px] relative flex items-center justify-center bg-[#1e3a8a] border border-blue-800 rounded-xl overflow-hidden selection:bg-blue-500 selection:text-white">
            {/* Blueprint Grid Background */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff15_1px,transparent_1px),linear-gradient(to_bottom,#ffffff15_1px,transparent_1px)] bg-[size:20px_20px]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_50%_50%,#1e3a8a_0%,#172554_100%)] opacity-80" />

            {/* Technical Markings */}
            <div className="absolute top-4 left-4 font-mono text-[10px] text-blue-300 opacity-60">DECISION_INT_V1.0 // DUAL_SYS</div>

            {/* Rulers/Guides */}
            <div className="absolute top-1/2 left-0 w-full h-px bg-blue-400/20" />
            <div className="absolute top-0 left-1/3 w-px h-full bg-blue-400/20" />
            <div className="absolute top-0 right-1/3 w-px h-full bg-blue-400/20" />

            {/* --- TOP LAYER: HUMAN COLLABORATION --- */}
            <div className="absolute top-6 left-0 w-full flex justify-center z-10">
                <div className="flex items-center gap-8">
                    {/* Label */}
                    <div className="text-right">
                        <span className="font-mono text-[10px] font-bold text-blue-200 block uppercase tracking-widest">Decision Intelligence</span>
                        <span className="font-mono text-[7px] text-blue-400 block tracking-wider">HUMAN-IN-THE-LOOP</span>
                    </div>

                    {/* Human/Perms Nodes */}
                    <div className="flex gap-4">
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="px-3 py-1.5 border border-blue-400/60 bg-blue-900/60 rounded flex items-center gap-2 backdrop-blur-sm shadow-lg"
                        >
                            <div className="w-1.5 h-1.5 bg-blue-300 rounded-full" />
                            <span className="font-mono text-[8px] font-bold text-white uppercase">Your Judgment</span>
                        </motion.div>

                        <div className="w-px h-6 bg-blue-400/40" />

                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="px-3 py-1.5 border border-blue-400/60 bg-blue-900/60 rounded flex items-center gap-2 backdrop-blur-sm shadow-lg"
                        >
                            <span className="font-mono text-[8px] font-bold text-white uppercase">Context</span>
                        </motion.div>

                        <div className="w-px h-6 bg-blue-400/40" />

                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="px-3 py-1.5 border border-blue-400/60 bg-blue-900/60 rounded flex items-center gap-2 backdrop-blur-sm shadow-lg"
                        >
                            <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-pulse" />
                            <span className="font-mono text-[8px] font-bold text-white uppercase">Human in the Loop</span>
                        </motion.div>
                    </div>
                </div>
                {/* Connection Line Down to Kernel */}
                <div className="absolute top-8 left-1/2 w-px h-32 border-r border-dashed border-blue-300/40 -z-10" />
            </div>

            <div className="relative z-10 w-full h-full flex items-center justify-between px-8 pt-16">

                {/* --- LEFT: INPUTS (Open Arch) --- */}
                <div className="flex flex-col gap-6 items-end relative mr-4">
                    {/* Input 1: Open Arch */}
                    <div className="relative flex items-center">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="w-36 h-8 border border-blue-400/50 bg-blue-900/40 backdrop-blur-sm rounded-sm flex items-center justify-end px-3 shadow-md hover:border-blue-300 cursor-pointer"
                        >
                            <span className="font-mono text-[9px] font-bold text-blue-200">MEETINGS</span>
                            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full ml-2" />
                        </motion.div>
                        <div className="absolute -right-8 w-8 h-px border-t border-dashed border-blue-300/50" />
                    </div>

                    {/* Input 2: BYO Model */}
                    <div className="relative flex items-center">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="w-36 h-8 border border-blue-400/50 bg-blue-900/40 backdrop-blur-sm rounded-sm flex items-center justify-end px-3 shadow-md hover:border-blue-300 cursor-pointer"
                        >
                            <span className="font-mono text-[9px] font-bold text-blue-200">EMAILS</span>
                            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full ml-2" />
                        </motion.div>
                        <div className="absolute -right-8 w-8 h-px border-t border-dashed border-blue-300/50" />
                    </div>

                    {/* Input 2b: BYO Agent */}
                    <div className="relative flex items-center">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="w-36 h-8 border border-blue-400/50 bg-blue-900/40 backdrop-blur-sm rounded-sm flex items-center justify-end px-3 shadow-md hover:border-blue-300 cursor-pointer"
                        >
                            <span className="font-mono text-[9px] font-bold text-blue-200">DOCS & DATA</span>
                            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full ml-2" />
                        </motion.div>
                        <div className="absolute -right-8 w-8 h-px border-t border-dashed border-blue-300/50" />
                    </div>

                    {/* Input 3: No Lock-in */}
                    <div className="relative flex items-center">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="w-36 h-8 border border-blue-400/50 bg-blue-900/40 backdrop-blur-sm rounded-sm flex items-center justify-end px-3 shadow-md hover:border-blue-300 cursor-pointer"
                        >
                            <span className="font-mono text-[9px] font-bold text-blue-200">DECISIONS</span>
                            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full ml-2" />
                        </motion.div>
                        <div className="absolute -right-8 w-8 h-px border-t border-dashed border-blue-300/50" />
                    </div>
                </div>


                {/* --- CENTER: PEER OS KERNEL --- */}
                <div className="relative w-40 h-40 border-2 border-blue-300/60 bg-blue-900/60 backdrop-blur-md flex flex-col items-center justify-center rounded-lg shadow-[0_0_30px_rgba(30,58,138,0.5)] z-20">
                    {/* Top Connector (From Human Layer) */}
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-6 h-3 border-l border-r border-b border-blue-300/50 rounded-b-sm bg-blue-900/40" />
                    <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-blue-300 rounded-full" />

                    {/* Corners */}
                    <div className="absolute -top-1 -left-1 w-2 h-2 border-t-2 border-l-2 border-blue-100" />
                    <div className="absolute -top-1 -right-1 w-2 h-2 border-t-2 border-r-2 border-blue-100" />
                    <div className="absolute -bottom-1 -left-1 w-2 h-2 border-b-2 border-l-2 border-blue-100" />
                    <div className="absolute -bottom-1 -right-1 w-2 h-2 border-b-2 border-r-2 border-blue-100" />

                    <div className="text-center mb-3">
                        <span className="font-mono font-bold text-base text-white tracking-widest block">DUAL HQ</span>
                    </div>

                    {/* Internal Modules Grid */}
                    <div className="grid grid-cols-2 gap-1.5 mb-2">
                        <div className="px-1.5 py-0.5 rounded border border-blue-500/30 bg-blue-800/50">
                            <span className="text-[6px] font-mono font-bold text-blue-200">MEMORY</span>
                        </div>
                        <div className="px-1.5 py-0.5 rounded border border-blue-500/30 bg-blue-800/50">
                            <span className="text-[6px] font-mono font-bold text-blue-200">LEARNING</span>
                        </div>
                    </div>
                    <div className="px-1.5 py-0.5 rounded border border-blue-500/30 bg-blue-800/50">
                        <span className="text-[6px] font-mono font-bold text-blue-200">SECURE</span>
                    </div>

                    {/* Integrations (Bottom) */}
                    <div className="absolute -bottom-16 flex flex-col items-center">
                        <div className="h-6 w-px border-l border-dashed border-emerald-400/50" />
                        <div className="flex gap-2">
                            <div className="px-2 py-0.5 border border-emerald-400/50 bg-emerald-900/30 rounded-full flex gap-1 items-center backdrop-blur-sm">
                                <div className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                                <span className="text-[7px] font-mono font-bold text-emerald-300">MCP</span>
                            </div>
                            <div className="px-2 py-0.5 border border-emerald-400/50 bg-emerald-900/30 rounded-full flex gap-1 items-center backdrop-blur-sm">
                                <span className="text-[7px] font-mono font-bold text-emerald-300">API</span>
                            </div>
                            <div className="px-2 py-0.5 border border-emerald-400/50 bg-emerald-900/30 rounded-full flex gap-1 items-center backdrop-blur-sm">
                                <span className="text-[7px] font-mono font-bold text-emerald-300">REAL-TIME</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Connection Line Center -> Right */}
                <div className="flex-1 h-px border-t border-dashed border-blue-300/40 mx-4" />


                {/* --- RIGHT: AGENTS SYSTEM --- */}
                <div className="flex items-center">

                    {/* Parent Block: "AGENTS" */}
                    <div className="h-40 w-10 border-2 border-blue-200 bg-blue-800/40 backdrop-blur-md rounded-md flex items-center justify-center relative z-10 shadow-lg mr-6">
                        <div className="absolute -top-1 -left-1 w-1.5 h-1.5 border-t-2 border-l-2 border-blue-100" />
                        <div className="absolute -bottom-1 -right-1 w-1.5 h-1.5 border-b-2 border-r-2 border-blue-100" />

                        <div className="text-center transform -rotate-90 whitespace-nowrap">
                            <span className="font-mono font-bold text-sm text-white tracking-widest block">DUALS</span>
                            <span className="font-mono text-[7px] text-blue-200 block mt-0.5 tracking-wider uppercase">Scale Yourself</span>
                        </div>

                        {/* Connector to Children (Right Side) */}
                        <div className="absolute right-[-1.5rem] top-1/2 w-6 h-px border-t border-blue-300/40" />
                        <div className="absolute right-[-1.5rem] top-1/2 h-[120px] w-px border-r border-blue-300/40 transform -translate-y-1/2" />
                    </div>

                    {/* Agent Types (Children) */}
                    <div className="flex flex-col gap-6 ml-4">
                        {/* 1. The Twin */}
                        <div className="relative flex items-center">
                            <div className="absolute -left-6 w-6 h-px border-t border-dashed border-blue-300/40" />
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                className="w-28 h-8 border border-blue-400/40 bg-blue-900/40 backdrop-blur-sm rounded flex items-center px-2 shadow-sm hover:border-blue-300 cursor-pointer"
                            >
                                <div className="w-1 h-1 bg-blue-300 rounded-full mr-2" />
                                <span className="font-mono text-[8px] font-bold text-blue-100">THE TWIN</span>
                            </motion.div>
                        </div>

                        {/* 2. The Proxy */}
                        <div className="relative flex items-center">
                            <div className="absolute -left-6 w-6 h-px border-t border-dashed border-blue-300/40" />
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                className="w-28 h-8 border border-blue-400/40 bg-blue-900/40 backdrop-blur-sm rounded flex items-center px-2 shadow-sm hover:border-blue-300 cursor-pointer"
                            >
                                <div className="w-1 h-1 bg-blue-300 rounded-full mr-2" />
                                <span className="font-mono text-[8px] font-bold text-blue-100">THE PROXY</span>
                            </motion.div>
                        </div>

                        {/* 3. The Scout */}
                        <div className="relative flex items-center">
                            <div className="absolute -left-6 w-6 h-px border-t border-dashed border-blue-300/40" />
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                className="w-28 h-8 border border-blue-400/40 bg-blue-900/40 backdrop-blur-sm rounded flex items-center px-2 shadow-sm hover:border-blue-300 cursor-pointer"
                            >
                                <div className="w-1 h-1 bg-red-400 rounded-full mr-2 animate-pulse" />
                                <span className="font-mono text-[8px] font-bold text-blue-100">THE SCOUT</span>
                            </motion.div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};
